import { Component, OnInit } from "@angular/core";
import { getValuesFromEnum } from "@app/class/Utilities";
import { VTableRowStatus } from "@app/directives/_layout/v-table/_enums/VTableRowStatus.enum";
import { IVTableCell } from "@app/directives/_layout/v-table/_models/IVTableCell";
import { VehicleOnlineStatusComponent } from "@app/directives/_misc/vehicle-online-status-indicator/vehicle-online-status.component";
import { DateRange } from "@app/enum/DateRange.enum";
import { SortDirection } from "@app/enum/SortDirection";
import { SortType } from "@app/enum/SortType";
import { IGoogleMapsMarker } from "@app/interfaces/IGoogleMapsMarkerOptions";
import { IUser } from "@app/interfaces/IUser";
import { VideoEventCode } from "@app/main/components/videos/enums/VideoEventCode";
import { CameraStatusService } from "@app/main/components/videos/services/camera-status.service";
import { VideoEntryService } from "@app/main/components/videos/services/video-entry.service";
import { VideoEntryUtils } from "@app/main/components/videos/utils/video-entry-utils";
import { DateService, DateUnit } from "@app/services/date.service";
import { UsersService } from "@app/services/users.service";
import { CameraDisplayService } from "@components/videos/cameraDisplay.service";
import { VideoStatus } from "@components/videos/enums/VideoStatus";
import { IVideoEntry } from "@components/videos/models/IVideoEntry";
import { IVTableColumn } from "@directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@directives/_layout/v-table/_models/IVTableRow";
import { IDateCellOptions, VTableService } from "@directives/_layout/v-table/_services/v-table.service";
import { IAggregateFilterEvent, IFilterBarTab } from "@directives/_resourceFilters/filter-bar/filter-bar.models";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { GeocodeService } from "@services/geocode.service";
import { TranslationService } from "@services/translation.service";
import { IPolyline } from "@typings/google.maps.angular";
import { Subscription, timer } from "rxjs";
import { VideoAddressComponent } from "../video-address/video-address.component";
import { VideoDownloadProgressComponent } from "../video-download-progress/video-download-progress.component";
import { VideoDriverAssignmentComponent } from "../video-driver-assignment/video-driver-assignment.component";
import { VideoDownloadIcons } from "@app/enum/VideoDownloadIcons";
import "./video-download-list.component.scss";

@UntilDestroy()
@Component({
    selector: "video-download-list",
    template: require("./video-download-list.component.html")
})
export class VideoDownloadListComponent implements OnInit {

    // raw data
    users: IUser[];
    videoEntries: IVideoEntry[];

    // display
    defaultDateRange = DateRange.Latest4Weeks;
    displayVideoEntries: IVideoEntry[];
    exportedFileName: string;
    sidebarTitle: string;

    selectedVideoEntry: IVideoEntry;
    videosAvailable: boolean;

    tableColumns: IVTableColumn<IVideoEntry>[];
    tableRows: IVTableRow[];

    filterTabs: IFilterBarTab<IVideoEntry>[] = [];
    filterEvent: IAggregateFilterEvent<IVideoEntry>;

    // sidebar map
    vehiclePolyline: IPolyline;
    startMapMarker: IGoogleMapsMarker;
    endMapMarker: IGoogleMapsMarker;

    // loading
    loading: boolean;
    firstLoadComplete: boolean;
    timerSubscription = new Subscription();

    eventsList: { translatedString: string, value: VideoEventCode }[];
    VideoStatus = VideoStatus;

    constructor(
        private readonly dateService: DateService,
        private readonly usersService: UsersService,
        private readonly cameraDisplayService: CameraDisplayService,
        private readonly vTableService: VTableService,
        private readonly translationService: TranslationService,
        private readonly geocodeService: GeocodeService,
        private readonly videoEntryService: VideoEntryService,
        private readonly cameraStatusService: CameraStatusService) {
    }

    ngOnInit() {
        this.createTabs();
        this.createExportName();
        this.createTableColumns();
        this.subscribeToCacheUpdates();
    }

    ngOnDestroy(): void {
        this.unsubscribeFromUpdates();
    }

    onTableRowClicked(row: IVTableRow<IVideoEntry>): void {
        const videoEntry: IVideoEntry = row.data;

        if (VideoEntryUtils.isDownloaded(videoEntry)) {
            this.cameraDisplayService.showVideoPlayer(videoEntry).then(result => {
                if (result.requiresUpdate) {
                    this.removeVideoEntryFromList(videoEntry);
                }
            });

            return;
        }

        this.selectVideoEntry(videoEntry);
    }

    selectVideoEntry(videoEntry: IVideoEntry): void {
        this.selectedVideoEntry = videoEntry;
    }

    clearSelectedVideoEntry(): void {
        this.selectedVideoEntry = null;
        this.videosAvailable = false;
    }

    openDownloadedVideo(videoEntry: IVideoEntry): void {
        this.cameraDisplayService.showVideoPlayer(videoEntry);
        this.clearSelectedVideoEntry();
    }

    filterVideoBySearchText(videoEntry: IVideoEntry, text: string): boolean {

        if (videoEntry.vehicle.ShortReg.toLocaleLowerCase().indexOf(text) >= 0 || videoEntry.vehicle.Reg.toLocaleLowerCase().indexOf(text) >= 0) {
            return true;
        }

        if (videoEntry.driver?.Name.toLocaleLowerCase().indexOf(text) >= 0) {
            return true;
        }

        return false;
    }


    filterVideoByEvent(videoEntry: IVideoEntry, event: any): boolean {
        return videoEntry.EventCode === event.value;
    }

    eventSearchPredicate(event: VideoEventCode, searchText: string): boolean {
        const translatedState = this.translationService.tEnum(VideoEventCode, "VideoEventCode", event);
        return translatedState.toLowerCase().indexOf(searchText.toLowerCase()) >= 0;
    }

    onFilterChanged(event: IAggregateFilterEvent<IVideoEntry>) {

        if (!event.trigger) {
            return;
        }

        this.filterEvent = event;

        if (this.filterEvent.trigger == "date") {
            this.subscribeToUpdates(true);
            return;
        }
    }

    onRequestComplete(): void {
        this.videosAvailable = true;
    }

    getVideoDownloadStatusLabel(videoEntry: IVideoEntry): string {
        return this.cameraStatusService.getVideoDownloadStatusLabel(videoEntry)
    }

    retryDownload(): void {
        this.selectedVideoEntry.Status = VideoStatus.UserUploadRequested;
        this.selectedVideoEntry.ExternalId = null;
        this.selectedVideoEntry.DateRequested = new Date();
        this.videoEntryService.updateVideoEntry(this.selectedVideoEntry).then(videoEntry => {
            this.selectedVideoEntry = videoEntry;
            this.clearSelectedVideoEntry();
        })
    }

    get isSelectedVideoStatusValid(): boolean {
        return !VideoEntryUtils.isDownloadFailed(this.selectedVideoEntry);
    }

    // ---

    private removeVideoEntryFromList(videoEntry: IVideoEntry): void {
        const filteredEntries = this.videoEntries.filter(x => x.Id !== videoEntry.Id)
        this.setVideoEntriesData(filteredEntries);
    }

    private subscribeToUpdates(showLoadingSpinner: boolean = false): void {

        if (showLoadingSpinner) {
            this.firstLoadComplete = false;
        }

        this.unsubscribeFromUpdates();

        const timer$ = timer(0, 60_000);

        this.timerSubscription = timer$.subscribe(() => {
            this.getData(true);
        });
    }

    private unsubscribeFromUpdates(): void {
        this.timerSubscription?.unsubscribe();
    }

    private async getData(invalidateCache: boolean = false): Promise<void> {
        this.loading = true;

        const videoEntries = await this.getVideoEntries(invalidateCache);
        const users = await (this.usersService.getUsers({ includeAdmins: true }) as Promise<IUser[]>);

        this.users = users;
        this.setVideoEntriesData(videoEntries);

        this.loading = false;
        this.firstLoadComplete = true;
    }

    private setVideoEntriesData(videoEntries: IVideoEntry[]): void {
        this.videoEntries = this.filterVideoEntries(videoEntries);

        if (this.selectedVideoEntry) {
            this.selectedVideoEntry = videoEntries.find(entry => entry.Id === this.selectedVideoEntry.Id);
        }

        this.processVideoEntries();
        this.createTableRows();
        this.createFilterItems();
    }

    private filterVideoEntries(videoEntries: IVideoEntry[]): IVideoEntry[] {
        return videoEntries.filter(v => v.Status != VideoStatus.Archived);
    }

    private getVideoEntries(invalidateCache: boolean = false): Promise<IVideoEntry[]> {

        const endTime = this.filterEvent.date[1];
        const startTime = this.filterEvent.date[0];

        return this.videoEntryService.getVideoEntriesRequestedWithMappings({
            endTime,
            startTime,
            invalidateCache,
            excludeMediaDeleted: true
        });
    }

    private processVideoEntries(): void {
        if (!this.videoEntries?.length) {
            return;
        }

        this.videoEntries.forEach(videoEntry => {
            if (!videoEntry.vehicle) {
                videoEntry.vehicle = {};
            }

            if (!videoEntry.RequestedBy) {
                return;
            }

            const user = this.users.filter(u => u.Id === videoEntry.RequestedBy);

            if (user.length > 0) {
                videoEntry.requestedByUser = user[0];
            }
        });
    }

    private createTableColumns(): void {

        this.tableColumns = [];

        this.tableColumns = [
            {
                header: this.vTableService.createTableHeader({
                    tKey: "VideoDownloadList.Requested",
                    width: 160,
                    sortType: SortType.Date,
                    sorted: true,
                    sortDirection: SortDirection.Descending
                }),
                createCellFunction: (videoEntry) => {

                    let requestDate = videoEntry.MetaData.UserRequestedUploadDate || videoEntry.DateRequested;

                    if (!videoEntry.RequestedBy) {
                        requestDate = videoEntry.DateCreated;
                    }

                    const dateCellOptions: IDateCellOptions = {
                        showRelativeTime: true
                    };

                    if (!VideoEntryUtils.isDownloaded(videoEntry)) {
                        dateCellOptions["cutOff"] = this.dateService.addOrSubtractTime(new Date(), -10, DateUnit.Day)
                    }

                    return this.vTableService.createDateCell(requestDate, dateCellOptions);
                }
            },


            {
                header: this.vTableService.createVehicleTableHeader(),
                createCellFunction: (videoEntry) => this.vTableService.createVehicleRegCell(videoEntry.vehicle)
            },

            {
                header: this.vTableService.createTableHeader({
                    tKey: 'Common.Driver',
                    width: 140,
                    minTableWidth: 1450,
                }),
                createCellFunction: (videoEntry) => {

                    return this.vTableService.createTableCell(
                        videoEntry.driver?.DisplayName || this.translationService.t('DriverSummaryPanel.NoDriver'),
                        {
                            classes: !videoEntry.driver ? 'text-muted' : '',
                            component: VideoDriverAssignmentComponent,
                            componentInputs: {
                                videoEntry: videoEntry
                            }
                        })
                }
            },

            {
                header: this.vTableService.createTableHeader({
                    tKey: "Common.Event",
                    width: 140,
                    minTableWidth: 1450
                }),
                createCellFunction: (videoEntry) => this.vTableService.createTableCell(videoEntry.displayEventCode)
            },
            {
                header: this.vTableService.createTableHeader({
                    tKey: "VideoDownloadList.VideoStartTime",
                    minTableWidth: 700,
                    width: 160,
                    sortType: SortType.Date
                }),
                createCellFunction: (videoEntry) => {
                    return this.vTableService.createDateCell(videoEntry.VideoStartTime, { showRelativeTime: true });
                }
            },
            {
                header: this.vTableService.createLocationInformationHeader({
                    minTableWidth: 1150,
                    width: null,
                }),
                createCellFunction: (videoEntry) => this.vTableService.createTableCell(videoEntry, { exportDataMethod: () => this.exportVideoEntryLocationMethod(videoEntry), component: VideoAddressComponent, componentInputs: { videoEntry: videoEntry } })
            },
            {
                header: this.vTableService.createTableHeader({
                    tKey: "VideoDownloadList.Camera",
                    minTableWidth: 950,
                    width: 160,
                    sortable: false
                }),
                createCellFunction: (videoEntry) => this.createOnlineStatusCell(videoEntry)
            },
            {
                header: this.vTableService.createTableHeader({
                    tKey: "Common.Status",
                    width: 160,
                    minTableWidth: 600
                }),
                createCellFunction: (videoEntry) => this.createVideoDownloadStatusCell(videoEntry)
            },
            {
                header: this.vTableService.createTableHeader({
                    tKey: "VideoDownloadList.Progress",
                    width: 100,
                    sortable: false,
                    excludeFromExport: true
                }),
                createCellFunction: (videoEntry) => {
                    switch (videoEntry.Status) {
                        case VideoStatus.Error:
                            return this.vTableService.createStatusCell(VTableRowStatus.Error);
                        case VideoStatus.NoFilesAvailable:
                        case VideoStatus.NoFootageAvailable:
                            return this.vTableService.createStatusCell(VTableRowStatus.Info);
                        default:
                            return this.vTableService.createTableCell(0, { component: VideoDownloadProgressComponent, componentInputs: { videoEntry: videoEntry } })
                    }
                }
            }
        ];
    }

    private createTableRows(): void {
        this.tableRows = [];

        if (!this.videoEntries?.length) {
            return;
        }

        this.tableRows = this.vTableService.createTableRows(this.videoEntries, this.tableColumns);
    }

    private createOnlineStatusCell(data: IVideoEntry): IVTableCell {
        return this.vTableService.createTableCell(data, {
            exportDataMethod: () => this.exportOnlineStatusMethod(data),
            component: VehicleOnlineStatusComponent,
            componentInputs: {
                vehicle: data.vehicle,
                live: false
            }
        })
    }

    private createVideoDownloadStatusCell(data: IVideoEntry): IVTableCell {
        const label = this.getVideoDownloadStatusLabel(data);
        const auditLog = data.MetaData?.AuditLogs?.[0] ?? "";

        var icons = [];

        if (auditLog.includes("Auto Download")) {
            icons.push(VideoDownloadIcons.AutoDownload);
        }
        if (auditLog.includes("Requested by AddSecure Team")) {
            icons.push(VideoDownloadIcons.FoundationDownload);
        }

        return this.vTableService.createVideoDownloadStatusCell(label, icons);
    }

    private exportOnlineStatusMethod(videoEntry: IVideoEntry): Promise<string> {
        return new Promise(resolve => {
            if (!videoEntry) {
                return resolve("N/A");
            }

            if (!videoEntry.vehicle) {
                return resolve(this.translationService.t(`VehicleOnlineStatus.UnknownVehicle`));
            }

            if (videoEntry.vehicle.onlineStatus.online) {
                return resolve(this.translationService.t(`VehicleOnlineStatus.CameraOnline`));
            }

            if (!videoEntry.vehicle.onlineStatus.lastOnlineTime) {
                return resolve(this.translationService.t(`VehicleOnlineStatus.CameraOffline`))
            }

            return resolve(this.dateService.relativeTime(videoEntry.vehicle.onlineStatus.lastOnlineTime))
        });
    }

    private exportVideoEntryLocationMethod(videoEntry: IVideoEntry): Promise<string> {
        return new Promise((resolve, reject) => {
            if (!videoEntry) {
                return resolve("N/A")
            }

            if (videoEntry.GeocodedAddressId) {
                return resolve(videoEntry.PlaceName);
            }

            return this.geocodeService.getLocationInformationFromVideo(videoEntry.Id).then(location => {
                return resolve(location.LocationDescription);
            }).catch((error) => {
                console.error("Error retrieving location: ", error);

                return resolve("");
            });
        });
    }

    private createTabs(): void {
        this.filterTabs = [];

        this.createTabDefinition("VideoDownloadList.FilterTabAll", v => v.Status != VideoStatus.Restricted);
        this.createTabDefinition("VideoDownloadList.FilterTabPending", v => VideoEntryUtils.isDownloading(v) || VideoEntryUtils.isRequested(v));
        this.createTabDefinition("VideoDownloadList.FilterTabComplete", v => VideoEntryUtils.isDownloaded(v));
        this.createTabDefinition("VideoDownloadList.FilterTabFailed", v => VideoEntryUtils.isDownloadFailed(v));
        this.createTabDefinition("VideoEventList.RestrictedEvents", v => v.Status == VideoStatus.Restricted);
    }

    private createTabDefinition(translationString: string, callback: (video: IVideoEntry) => boolean) {
        this.filterTabs.push({ title: this.translationService.t(translationString), predicate: callback });
    }

    private createExportName(): void {
        this.exportedFileName = this.translationService.t("VideoDownloadList.ExportTitle");
    }

    private createFilterItems<T>(): any {
        this.eventsList = [];

        const enumValues = getValuesFromEnum(VideoEventCode);

        this.eventsList = enumValues.map(value => {
            return {
                translatedString: this.translationService.tEnum(VideoEventCode, "VideoEventCode", value),
                value: value
            }
        });
    }

    private subscribeToCacheUpdates(): void {
        this.videoEntryService.videoUpdate$.pipe(untilDestroyed(this)).subscribe(videoEntry => {
            this.videoEntries = [
                ...this.videoEntries.filter(e => e.Id !== videoEntry.Id),
                videoEntry
            ];

            this.createTableRows();
        })
    }
}
